import type {
   ProjectDetailFormValues,
   ProjectRoleData,
   ProjectWageOverrideData,
   RolesByJobTitleGroup,
} from "./types";
import { getAttachedDate } from "@laborchart-modules/common/dist/datetime";
import { DateUtils } from "@/lib/utils/date";
import { v4 } from "uuid";
import type { CannedMessage } from "@laborchart-modules/common";
import { stripAndReturnHTMLTokens } from "../../communications/dynamic-tokens/token-utils";
import type { CannedMessageType } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import type {
   CustomField,
   IntegratedField,
   ResourceName,
   StreamResponseConversion,
} from "@/react/prop-types";
import type { ExtendedGetDetailResponse } from "../types";
import { TIME_ZONES_MAP } from "@/lib/utils/timezones";
import { convertDateToUTCDateWithoutTimezone } from "@/lib/utils/date-2";
import { isIntegratedField } from "@/react/shared/helper";
import { processCustomFieldsWithPermission } from "@/react/shared/custom-field-utils";

export function compareDates({
   startDate,
   endDate,
}: {
   startDate: Date;
   endDate: ProjectDetailFormValues["est_end_date"] | undefined;
}) {
   if (!startDate) {
      return true;
   }
   const formattedStartDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
   );
   if (endDate) {
      const formattedEndDate = new Date(
         endDate.getFullYear(),
         endDate.getMonth(),
         endDate.getDate(),
      );
      if (formattedEndDate.getTime() === formattedStartDate.getTime()) {
         return true;
      }
      return formattedEndDate.getTime() > formattedStartDate.getTime();
   }
   return true;
}

export function prepareOverridePayload(selectedOverrideId: string, wage: number | null) {
   const payload: ProjectWageOverrideData = {
      wage_overrides: {
         [selectedOverrideId]: wage,
      },
   };
   return payload;
}

export function formatFullDateTime(timestamp: number): string {
   if (isNaN(timestamp) || timestamp < 0) {
      return "";
   }
   const date = new Date(timestamp);
   const month = DateUtils.getMonthAbbreviation(date);
   const day = date.getDate();
   const year = date.getFullYear();
   const time = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
   const formatter = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" });
   const timeZoneName =
      formatter.formatToParts(date).find((part) => part.type === "timeZoneName")?.value ?? "";
   return `${month} ${day}, ${year} at ${time} ${timeZoneName}`;
}

export function getInitials(fullName: string): string {
   const words = fullName.split(" ");
   if (words.length < 2) {
      return "";
   }
   const initials = words.slice(0, 2).map((word) => word.charAt(0).toUpperCase());
   return initials.join("");
}

function processDataForView(breakdown: any, selectedView: string): Array<[number, number]> {
   const assignments = breakdown.daily_ass_totals;
   const requests = breakdown.daily_request_totals;
   const data: Array<[number, number]> = [];

   const processEntry = (k: string, yVal: number) => {
      data.push([getAttachedDate(Number(k)).getTime(), yVal]);
   };

   if (selectedView === "both") {
      const keys =
         Object.keys(requests).length >= Object.keys(assignments).length ? requests : assignments;
      Object.keys(keys).forEach((k) => {
         const yVal = (requests[k]?.count || 0) + (assignments[k]?.count || 0);
         processEntry(k, yVal);
      });
   } else {
      const source = selectedView === "requests" ? requests : assignments;
      Object.keys(source).forEach((k) => processEntry(k, source[k].count));
   }

   return data;
}

export function processTotalsDataForGraph(
   totals: any,
   selectedView: "assignments" | "requests" | "both",
   I18n: {
      t: (t: string) => string;
   },
): Highcharts.SeriesAreaOptions[] {
   return totals.breakdown_data
      .map((breakdown: any) => ({
         name: breakdown.name ?? I18n.t("views.company.workforce_planning.none"),
         color: breakdown.color ?? "#ACB5B9",
         type: "area" as const,
         data: processDataForView(breakdown, selectedView),
      }))
      .filter((series: Highcharts.SeriesAreaOptions) => {
         if (series.data) {
            return series.data.length > 0;
         }
         return false;
      });
}

export const getDateString = (date: Date) => {
   return `${DateUtils.getMonth(date)} ${date.getDate()}, ${date.getFullYear()}`;
};

// this function is used to group the roles by job title
export const getGroupedDataForRolesByJobTitle = (
   rolesData: ExtendedGetDetailResponse["data"]["roles"] = [],
): RolesByJobTitleGroup[] => {
   const groupedData = rolesData.reduce((acc: { [key: string]: any }, item) => {
      const { job_title_id, assignee, id, person_id, position } = item;

      // If the group doesn't exist, initialize it
      if (!acc[job_title_id]) {
         acc[job_title_id] = {
            job_title_id,
            job_title_name: position.name,
            job_title_color: position.color,
            persons: [],
            sequence: position.sequence,
         };
      }
      // Push the person details into the group
      acc[job_title_id].persons.push({
         role_id: id,
         id: person_id,
         name: `${assignee.name.first} ${assignee.name.last}`,
         email: assignee.email,
      });
      // sorting by name
      acc[job_title_id].persons.sort((a: { name: string }, b: { name: string }) => {
         return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });

      return acc;
   }, {});

   // Convert grouped data to an array and sort by sequence
   const sortedGroupedData = Object.values(groupedData).sort(
      (a: { sequence: number }, b: { sequence: number }) => a.sequence - b.sequence,
   );
   return sortedGroupedData;
};

// this function is used to get the removed resources while updating the roles
export const getRemovedResources = (
   initialResources: ResourceName[],
   selectedResources: ResourceName[],
) => {
   // Find resources in initialResources that are not in selectedResources
   const removedResources = initialResources.filter(
      (initial) => !selectedResources.some((selected) => selected.id === initial.id),
   );

   // Add archived flag to removed resources
   return removedResources.map((resource) => ({
      ...resource,
      archived: true,
   }));
};

export function prepareRolesPayload(
   jobTitleValue: string,
   resources: ResourceName[],
   archived: boolean,
) {
   // Prepare roles payload by mapping the resources to roles array
   const roles = resources.map((resource) => ({
      id: resource.role_id ?? v4(),
      position_id: jobTitleValue,
      person_id: resource.id,
      archived: resource.archived ?? archived,
   }));

   const payload: ProjectRoleData = {
      roles,
   };
   return payload;
}

export function prepareCannedMessagePayload(
   alertContent: { subject: string; content: string },
   formValues: any,
   projectId: string,
   type: CannedMessageType,
): Partial<CannedMessage> {
   const { content: apiReadySubject, dynamicTokens: subjectTokens } = stripAndReturnHTMLTokens(
      alertContent.subject,
      projectId,
   );
   const { content: apiReadyContent, dynamicTokens: contentTokens } = stripAndReturnHTMLTokens(
      alertContent.content,
      projectId,
   );

   const dynamic_tokens = subjectTokens.concat(contentTokens);

   const payload = {
      content: apiReadyContent,
      subject: apiReadySubject,
      include_signature: false,
      is_group: formValues.send_as.value === "group",
      owner_id: projectId,
      type: type,
      dynamic_tokens: dynamic_tokens,
   };

   return payload;
}

export function getCustomAlertsInitialValues(cannedMessage: CannedMessage | null, I18n: any) {
   if (!cannedMessage || !cannedMessage.is_group) {
      return {
         send_as: {
            id: "individual",
            value: "individual",
            label: I18n.t("views.company.workforce_planning.communications.individual_messages"),
         },
      };
   } else {
      return {
         send_as: {
            id: "group",
            value: "group",
            label: I18n.t("views.company.workforce_planning.communications.group_messages"),
         },
      };
   }
}

export function getCustomAssignmentsTearsheetHeader(I18n: any, type: CannedMessageType): string {
   switch (type) {
      case "assignment-new":
         return I18n.t("views.company.workforce_planning.communications.customized_new_assignment");
      case "assignment-edit":
         return I18n.t(
            "views.company.workforce_planning.communications.customized_updated_assignment",
         );
      case "assignment-transfer":
         return I18n.t(
            "views.company.workforce_planning.communications.customized_assignment_transfer",
         );
      case "assignment-delete":
         return I18n.t(
            "views.company.workforce_planning.communications.customized_deleted_assignment",
         );
      default:
         return I18n.t("views.company.workforce_planning.communications.assignment_alert");
   }
}

export type BuildProjectUpdatePayloadParams = {
   values: ProjectDetailFormValues;
   initialValues: ProjectDetailFormValues | undefined;
   groupOptions: StreamResponseConversion;
   integratedFields: IntegratedField[];
   customFields?: CustomField[];
   canEditProjectSensitiveFields: boolean;
   projectSensitiveFields: string[];
   canViewProjectFinancials: boolean;
};

export function buildProjectUpdatePayload({
   values,
   initialValues,
   groupOptions,
   integratedFields,
   customFields,
   canEditProjectSensitiveFields,
   projectSensitiveFields,
   canViewProjectFinancials,
}: BuildProjectUpdatePayloadParams): Record<string, any> {
   // Transform group IDs: if "select_all" is selected, all group IDs will be true
   const selectedGroupIds = values.group_ids.map((g: { id: string }) => g.id);
   const group_ids = groupOptions.reduce<Record<string, boolean>>(
      (acc, { id }) => ({
         ...acc,
         [id]: selectedGroupIds.includes("select_all") || selectedGroupIds.includes(id),
      }),
      {},
   );

   // Build base payload from form values
   const payload: Record<string, any> = {
      name: values.name,
      job_number: values.job_number,
      status: values.status.id,
      group_ids,
      daily_start_time: values.daily_start_time.id,
      daily_end_time: values.daily_end_time.id,
      timezone: TIME_ZONES_MAP[values.timezone?.id] ?? null,
      address_1: values.address_1,
      address_2: values.address_2,
      city_town: values.city_town,
      state_province: values.state_province,
      country: values.country,
      zipcode: values.zipcode,
      bid_rate: values.bid_rate,
      color: values.color,
      customer_name: values.customer_name,
      percent_complete: values.percent_complete,
      project_type: values.project_type,
   };

   // Only include dates that changed relative to initialValues
   if (values.start_date?.getTime() !== initialValues?.start_date?.getTime()) {
      payload.start_date = convertDateToUTCDateWithoutTimezone(values.start_date);
   }
   if (values.est_end_date?.getTime() !== initialValues?.est_end_date?.getTime()) {
      payload.est_end_date = convertDateToUTCDateWithoutTimezone(values.est_end_date);
   }

   // Remove integrated fields from payload as they are not editable
   Object.keys(payload).forEach((fieldName) => {
      if (isIntegratedField(fieldName, integratedFields)) {
         delete payload[fieldName];
      }
   });

   // Remove sensitive fields from payload if user does not have permission to edit them
   Object.keys(payload).forEach((fieldName) => {
      if (!canEditProjectSensitiveFields && projectSensitiveFields.includes(fieldName)) {
         delete payload[fieldName];
      }
   });

   // Remove financial fields if user cannot view financials
   if (!canViewProjectFinancials) {
      delete payload.bid_rate;
   }

   // Process and add custom fields to payload
   if (customFields) {
      payload.custom_fields = processCustomFieldsWithPermission(
         customFields,
         values,
         integratedFields,
         canViewProjectFinancials,
         projectSensitiveFields,
         canEditProjectSensitiveFields,
      );
   }

   return payload;
}
